/**
 * PlatformUsageView — `/platform/usage` (Doc 25 §F4).
 *
 * Pantalla cross-tenant para platform admins. Tres tabs:
 *
 *   1. Global — KPIs HIVA (costo real vs costo de venta + margen).
 *   2. Por tenant — selector + summary scoped al tenant elegido.
 *   3. Margen — tabla por tenant con margen $ y %.
 *
 * Toda la pantalla está gateada al sidebar por `me.is_platform_admin`.
 * Sin ese flag el sidebar no muestra el item — y si igual navega el
 * user, el backend rechaza con 403.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2 } from 'lucide-react'

import { ScrollPage } from '../../components/shell/ScrollPage'
import {
  formatDateTime,
  formatInt,
  formatUsd,
  resolvePeriod,
  type PeriodPreset,
} from '../../components/usage/usageHelpers'
import {
  usePlatformGlobal,
  usePlatformMargin,
  usePlatformTenantSummary,
  usePlatformTenants,
  useRunBalanceCheck,
  useRunBillingVerification,
} from '../../hooks/usePlatform'

type TabKey = 'global' | 'per_tenant' | 'margin' | 'maintenance'

const PERIOD_PRESETS: readonly PeriodPreset[] = [
  'last_7d',
  'last_30d',
  'last_90d',
  'this_month',
]

export function PlatformUsageView() {
  const { t, i18n } = useTranslation(['platform', 'usage'])
  const locale = i18n.language
  const [tab, setTab] = useState<TabKey>('global')
  const [period, setPeriod] = useState<PeriodPreset>('last_30d')
  const { from, to } = useMemo(() => resolvePeriod(period), [period])

  return (
    <ScrollPage>
      <div className="max-w-7xl">
        <header className="mb-6">
          <h1 className="font-display text-2xl font-semibold text-ink">
            {t('platform:page_title')}
          </h1>
          <p className="mt-1 font-body text-sm text-ink-muted max-w-prose">
            {t('platform:page_subtitle')}
          </p>
        </header>

        {/* Tabs */}
        <div className="flex items-center gap-1 border-b border-line mb-6">
          {(['global', 'per_tenant', 'margin', 'maintenance'] as TabKey[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setTab(k)}
              className={
                k === tab
                  ? 'px-4 py-2 text-sm font-medium border-b-2 border-brand text-brand -mb-px'
                  : 'px-4 py-2 text-sm text-ink-muted hover:text-ink'
              }
            >
              {t(`platform:tabs.${k}`)}
            </button>
          ))}
        </div>

        {/* Period picker compartido */}
        <div className="flex items-center gap-2 flex-wrap mb-6">
          <span className="text-xs font-medium text-ink-subtle">
            {t('usage:period.label')}:
          </span>
          {PERIOD_PRESETS.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => setPeriod(p)}
              className={
                p === period
                  ? 'rounded-md border border-brand bg-brand text-canvas px-3 py-1 text-xs font-medium'
                  : 'rounded-md border border-line bg-surface hover:bg-surface-muted px-3 py-1 text-xs text-ink-muted'
              }
            >
              {t(`usage:period.${p}`)}
            </button>
          ))}
        </div>

        {tab === 'global' && (
          <GlobalTab from={from} to={to} locale={locale} t={t} />
        )}
        {tab === 'per_tenant' && (
          <PerTenantTab from={from} to={to} locale={locale} t={t} />
        )}
        {tab === 'margin' && (
          <MarginTab from={from} to={to} locale={locale} t={t} />
        )}
        {tab === 'maintenance' && <MaintenanceTab locale={locale} t={t} />}
      </div>
    </ScrollPage>
  )
}

// ─── Global tab ───────────────────────────────────────────────────────────────
function GlobalTab({
  from,
  to,
  locale,
  t,
}: {
  from: string
  to: string
  locale: string
  t: ReturnType<typeof useTranslation>['t']
}) {
  const q = usePlatformGlobal({ from, to })
  // TanStack Query v5: `isPending` cubre tanto la primera carga como
  // el estado `enabled: false` (mientras MSAL aún no entrega el token).
  // Usar `isLoading` aquí dejaría caer la rama en el "error" rojo de
  // abajo cuando data=undefined+isLoading=false (state disabled).
  if (q.isPending) {
    return (
      <div className="flex items-center gap-2 text-sm text-ink-muted">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('usage:records.loading')}
      </div>
    )
  }
  if (q.error) {
    return (
      <div className="rounded-lg border border-critical/40 bg-critical/5 px-4 py-3 text-sm text-critical">
        {t('usage:loading_error')}
      </div>
    )
  }
  const g = q.data
  if (!g) {
    return (
      <div className="rounded-lg border border-line bg-surface px-4 py-6 text-center text-sm text-ink-muted">
        {t('platform:global.no_data')}
      </div>
    )
  }
  if (g.calls === 0) {
    return (
      <div className="rounded-lg border border-line bg-surface px-4 py-6 text-center text-sm text-ink-muted">
        {t('platform:global.no_data')}
      </div>
    )
  }
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          label={t('platform:global.kpi_provider')}
          value={formatUsd(g.cost_provider_usd, locale)}
        />
        <KpiCard
          label={t('platform:global.kpi_billed')}
          value={formatUsd(g.cost_billed_usd, locale)}
          accent="brand"
        />
        <KpiCard
          label={t('platform:global.kpi_margin')}
          value={formatUsd(g.margin_usd, locale)}
          accent="official"
        />
        <KpiCard
          label={t('platform:global.kpi_margin_pct')}
          value={
            g.margin_pct !== null
              ? `${Number(g.margin_pct).toFixed(1)}%`
              : '—'
          }
          accent="official"
        />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiCard
          label={t('platform:global.kpi_tenants_active')}
          value={formatInt(g.tenants_active, locale)}
        />
        <KpiCard
          label={t('platform:global.kpi_calls')}
          value={formatInt(g.calls, locale)}
        />
        <KpiCard
          label={t('platform:global.kpi_tokens')}
          value={formatInt(g.tokens_total, locale)}
        />
      </div>
    </div>
  )
}

// ─── Per-tenant tab ───────────────────────────────────────────────────────────
function PerTenantTab({
  from,
  to,
  locale,
  t,
}: {
  from: string
  to: string
  locale: string
  t: ReturnType<typeof useTranslation>['t']
}) {
  const tenants = usePlatformTenants()
  const [selected, setSelected] = useState<string | null>(null)
  const summary = usePlatformTenantSummary(
    selected,
    { from, to },
    { enabled: !!selected },
  )

  const selectedTenant = useMemo(
    () => tenants.data?.find((tn) => tn.tenant_id === selected) ?? null,
    [tenants.data, selected],
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <label className="text-xs text-ink-muted">
          {t('platform:per_tenant.selector_label')}:
        </label>
        <select
          value={selected ?? ''}
          onChange={(e) => setSelected(e.target.value || null)}
          className="rounded-md border border-line bg-surface px-2 py-1 text-sm min-w-[260px]"
        >
          <option value="">
            {t('platform:per_tenant.selector_placeholder')}
          </option>
          {(tenants.data ?? []).map((tn) => (
            <option key={tn.tenant_id} value={tn.tenant_id}>
              {tn.display_name} ({tn.slug})
            </option>
          ))}
        </select>
      </div>

      {!selected && (
        <div className="rounded-lg border border-line bg-surface px-4 py-6 text-center text-sm text-ink-muted">
          {t('platform:per_tenant.empty')}
        </div>
      )}

      {selected && summary.isLoading && (
        <div className="flex items-center gap-2 text-sm text-ink-muted">
          <Loader2 className="w-4 h-4 animate-spin" />
          {t('usage:records.loading')}
        </div>
      )}

      {selected && summary.data && (
        <div className="space-y-4">
          {selectedTenant && (
            <div className="rounded-lg border border-line bg-surface-muted px-4 py-2 text-xs text-ink-muted">
              {t('platform:per_tenant.markup_label')}:{' '}
              <span className="font-mono text-ink">
                {selectedTenant.markup_percent
                  ? `${Number(selectedTenant.markup_percent).toFixed(2)}%`
                  : t('platform:per_tenant.markup_default')}
              </span>
            </div>
          )}
          {summary.data.calls === 0 ? (
            <div className="rounded-lg border border-line bg-surface px-4 py-6 text-center text-sm text-ink-muted">
              {t('platform:per_tenant.no_data_for_tenant')}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard
                label={t('platform:global.kpi_provider')}
                value={formatUsd(summary.data.cost_provider_usd, locale)}
              />
              <KpiCard
                label={t('platform:global.kpi_billed')}
                value={formatUsd(summary.data.cost_billed_usd, locale)}
                accent="brand"
              />
              <KpiCard
                label={t('platform:global.kpi_margin')}
                value={formatUsd(summary.data.margin_usd, locale)}
                accent="official"
              />
              <KpiCard
                label={t('platform:global.kpi_margin_pct')}
                value={
                  summary.data.margin_pct !== null
                    ? `${Number(summary.data.margin_pct).toFixed(1)}%`
                    : '—'
                }
                accent="official"
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ─── Margin tab ───────────────────────────────────────────────────────────────
function MarginTab({
  from,
  to,
  locale,
  t,
}: {
  from: string
  to: string
  locale: string
  t: ReturnType<typeof useTranslation>['t']
}) {
  const q = usePlatformMargin({ from, to })
  // Misma fix que GlobalTab — `isPending` cubre el estado disabled de
  // TanStack v5 cuando el token aún no llega.
  if (q.isPending) {
    return (
      <div className="flex items-center gap-2 text-sm text-ink-muted">
        <Loader2 className="w-4 h-4 animate-spin" />
        {t('usage:records.loading')}
      </div>
    )
  }
  if (q.error) {
    return (
      <div className="rounded-lg border border-critical/40 bg-critical/5 px-4 py-3 text-sm text-critical">
        {t('usage:loading_error')}
      </div>
    )
  }
  if (!q.data || q.data.rows.length === 0) {
    return (
      <div className="rounded-lg border border-line bg-surface px-4 py-6 text-center text-sm text-ink-muted">
        {t('platform:margin.empty')}
      </div>
    )
  }
  return (
    <div className="rounded-lg border border-line bg-surface overflow-x-auto">
      <table className="w-full text-xs">
        <thead className="bg-surface-muted text-ink-subtle">
          <tr>
            <th className="text-left px-4 py-2 font-medium">
              {t('platform:margin.col.tenant')}
            </th>
            <th className="text-right px-4 py-2 font-medium">
              {t('platform:margin.col.markup')}
            </th>
            <th className="text-right px-4 py-2 font-medium">
              {t('platform:margin.col.calls')}
            </th>
            <th className="text-right px-4 py-2 font-medium">
              {t('platform:margin.col.cost_provider')}
            </th>
            <th className="text-right px-4 py-2 font-medium">
              {t('platform:margin.col.cost_billed')}
            </th>
            <th className="text-right px-4 py-2 font-medium">
              {t('platform:margin.col.margin')}
            </th>
            <th className="text-right px-4 py-2 font-medium">
              {t('platform:margin.col.margin_pct')}
            </th>
            <th className="text-right px-4 py-2 font-medium">
              {t('platform:margin.col.balance')}
            </th>
            <th className="text-right px-4 py-2 font-medium">
              {t('platform:margin.col.last_topup')}
            </th>
          </tr>
        </thead>
        <tbody>
          {q.data.rows.map((row) => (
            <tr key={row.tenant_id} className="border-t border-line">
              <td className="px-4 py-2">
                <div className="font-medium text-ink">
                  {row.display_name}
                </div>
                <div className="font-mono text-[10px] text-ink-subtle">
                  {row.slug}
                </div>
              </td>
              <td className="text-right px-4 py-2 tabular-nums">
                {row.markup_percent
                  ? `${Number(row.markup_percent).toFixed(0)}%`
                  : '—'}
              </td>
              <td className="text-right px-4 py-2 tabular-nums">
                {formatInt(row.calls, locale)}
              </td>
              <td className="text-right px-4 py-2 tabular-nums">
                {formatUsd(row.cost_provider_usd, locale)}
              </td>
              <td className="text-right px-4 py-2 tabular-nums font-medium">
                {formatUsd(row.cost_billed_usd, locale)}
              </td>
              <td className="text-right px-4 py-2 tabular-nums text-official font-medium">
                {formatUsd(row.margin_usd, locale)}
              </td>
              <td className="text-right px-4 py-2 tabular-nums text-official">
                {row.margin_pct !== null
                  ? `${Number(row.margin_pct).toFixed(1)}%`
                  : '—'}
              </td>
              <td className="text-right px-4 py-2 tabular-nums">
                {row.balance_usd !== null
                  ? formatUsd(row.balance_usd, locale)
                  : '—'}
              </td>
              <td className="text-right px-4 py-2 tabular-nums text-[10px] text-ink-muted">
                {formatDateTime(row.last_topup_at, locale)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Maintenance tab (Doc 25 §F5) ────────────────────────────────────────────
// El cron real lo cablea F.pre como ACA Job. Ver:
//   hivanexus-infra/Docs/14-AZURE_DEPLOYMENT.md §"Scheduled Jobs (F.pre cabling)"
//   hivanexus-backend/app/tasks/maintenance.py (TODO block al inicio)
function MaintenanceTab({
  locale,
  t,
}: {
  locale: string
  t: ReturnType<typeof useTranslation>['t']
}) {
  const balanceCheck = useRunBalanceCheck()
  const billingVerify = useRunBillingVerification()

  return (
    <div className="space-y-6">
      <header>
        <h2 className="font-display text-lg font-semibold text-ink">
          {t('platform:maintenance.title')}
        </h2>
        <p className="mt-1 font-body text-sm text-ink-muted max-w-prose">
          {t('platform:maintenance.subtitle')}
        </p>
      </header>

      {/* Balance check card */}
      <section className="rounded-lg border border-line bg-surface p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-prose">
            <h3 className="font-display text-base font-semibold text-ink">
              {t('platform:maintenance.balance_check.button')}
            </h3>
            <p className="mt-1 font-body text-sm text-ink-muted">
              {t('platform:maintenance.balance_check.description')}
            </p>
          </div>
          <button
            type="button"
            disabled={balanceCheck.isPending}
            onClick={() => balanceCheck.mutate()}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 font-ui text-sm text-canvas hover:bg-brand-deep transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand"
          >
            {balanceCheck.isPending && (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
            {balanceCheck.isPending
              ? t('platform:maintenance.balance_check.running')
              : t('platform:maintenance.balance_check.button')}
          </button>
        </div>
        {balanceCheck.error && (
          <div className="mt-4 rounded-md border border-critical/30 bg-critical-soft px-3 py-2 text-xs text-critical">
            {t('platform:maintenance.error')}
          </div>
        )}
        {balanceCheck.data && (
          <div className="mt-4 rounded-md border border-line bg-surface-muted px-3 py-2 text-xs text-ink tabular-nums">
            {t('platform:maintenance.balance_check.result', {
              evaluated: balanceCheck.data.tenants_evaluated,
              alerted: balanceCheck.data.tenants_alerted,
              cooldown: balanceCheck.data.tenants_skipped_cooldown,
              sent: balanceCheck.data.emails_sent,
              failed: balanceCheck.data.emails_failed,
            })}
          </div>
        )}
      </section>

      {/* Billing verification card */}
      <section className="rounded-lg border border-line bg-surface p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="max-w-prose">
            <h3 className="font-display text-base font-semibold text-ink">
              {t('platform:maintenance.billing_verify.button')}
            </h3>
            <p className="mt-1 font-body text-sm text-ink-muted">
              {t('platform:maintenance.billing_verify.description')}
            </p>
          </div>
          <button
            type="button"
            disabled={billingVerify.isPending}
            onClick={() => billingVerify.mutate()}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 font-ui text-sm text-canvas hover:bg-brand-deep transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-brand"
          >
            {billingVerify.isPending && (
              <Loader2 className="w-4 h-4 animate-spin" />
            )}
            {billingVerify.isPending
              ? t('platform:maintenance.billing_verify.running')
              : t('platform:maintenance.billing_verify.button')}
          </button>
        </div>
        {billingVerify.error && (
          <div className="mt-4 rounded-md border border-critical/30 bg-critical-soft px-3 py-2 text-xs text-critical">
            {t('platform:maintenance.error')}
          </div>
        )}
        {billingVerify.data && (
          <div className="mt-4 space-y-3">
            <div className="rounded-md border border-line bg-surface-muted px-3 py-2 text-xs text-ink tabular-nums">
              {billingVerify.data.drifts_detected === 0
                ? t('platform:maintenance.billing_verify.result_ok', {
                    evaluated: billingVerify.data.tenants_evaluated,
                  })
                : t('platform:maintenance.billing_verify.result_drift', {
                    evaluated: billingVerify.data.tenants_evaluated,
                    drifts: billingVerify.data.drifts_detected,
                  })}
            </div>
            {billingVerify.data.drifts.length > 0 && (
              <div className="rounded-lg border border-critical/30 bg-surface overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-critical-soft text-ink-subtle">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium">
                        {t('platform:maintenance.billing_verify.drift_col.tenant')}
                      </th>
                      <th className="text-right px-4 py-2 font-medium">
                        {t('platform:maintenance.billing_verify.drift_col.recorded')}
                      </th>
                      <th className="text-right px-4 py-2 font-medium">
                        {t('platform:maintenance.billing_verify.drift_col.records_sum')}
                      </th>
                      <th className="text-right px-4 py-2 font-medium">
                        {t('platform:maintenance.billing_verify.drift_col.delta')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {billingVerify.data.drifts.map((d) => (
                      <tr
                        key={d.tenant_id}
                        className="border-t border-line"
                      >
                        <td className="px-4 py-2 font-mono text-[11px] text-ink">
                          {d.tenant_slug}
                        </td>
                        <td className="text-right px-4 py-2 tabular-nums">
                          {formatUsd(d.recorded_consumed_usd, locale)}
                        </td>
                        <td className="text-right px-4 py-2 tabular-nums">
                          {formatUsd(d.records_sum_usd, locale)}
                        </td>
                        <td className="text-right px-4 py-2 tabular-nums text-critical font-medium">
                          {formatUsd(d.delta_usd, locale)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  )
}

// ─── KPI Card ────────────────────────────────────────────────────────────────
function KpiCard({
  label,
  value,
  accent,
}: {
  label: string
  value: string
  accent?: 'brand' | 'official'
}) {
  const borderClass =
    accent === 'brand'
      ? 'border-brand/30'
      : accent === 'official'
        ? 'border-official/30'
        : 'border-line'
  return (
    <div className={`rounded-lg border ${borderClass} bg-surface px-4 py-3`}>
      <div className="text-[10px] uppercase tracking-wider font-medium text-ink-subtle">
        {label}
      </div>
      <div className="mt-1 font-display text-2xl font-semibold text-ink">
        {value}
      </div>
    </div>
  )
}
